// ============================================================================
// Sezione "Rubriche & Linee Guida" della dashboard che condividiamo col cliente.
//
// Mostra le stesse informazioni che il team compila nella pagina Copywriter:
// le rubriche/format ricorrenti e il brief creativo (obiettivi, taglio dei reel,
// cosa dire e cosa non dire). Il pulsante nella barra compare solo se c'è
// qualcosa da mostrare: la logica sta in ClientCalendar, qui si rende soltanto.
// ============================================================================

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Repeat, Target, Film, CheckCircle2, Ban, FileText, ChevronDown } from 'lucide-react';

export interface Rubrica {
  id: string;
  tipo: string;
  titolo: string;
  descrizione: string | null;
}

export interface Creative {
  obiettivi: string | null;
  taglio: string | null;
  colori: string[] | null;
  da_dire: string | null;
  da_non_dire: string | null;
  note: string | null;
}

export interface Guidelines {
  rubriche: Rubrica[];
  creative: Creative | null;
}

// Testo e icone in nero: il giallo ByMarke resta solo come riempimento (mai come colore di scritte).
const Y = '#13161C';

const pieno = (v?: string | null): boolean => typeof v === 'string' && v.trim().length > 0;

/**
 * Card di una rubrica: in elenco si legge solo l'anteprima (due righe), il
 * testo completo si apre con un click. Serve a far scorrere l'occhio sulle
 * rubriche senza doversi leggere tutte le descrizioni.
 */
const CardRubrica: React.FC<{ rubrica: Rubrica }> = ({ rubrica }) => {
  const [aperta, setAperta] = useState(false);
  const haTesto = pieno(rubrica.descrizione);

  // "Leggi di più" solo se il testo è davvero tagliato dalle due righe:
  // altrimenti il click non farebbe niente. Si rimisura al cambio di larghezza,
  // perché su schermi stretti le stesse parole occupano più righe.
  const testoRef = useRef<HTMLParagraphElement>(null);
  const [troncato, setTroncato] = useState(false);
  useLayoutEffect(() => {
    if (aperta) return;
    const misura = () => {
      const el = testoRef.current;
      if (el) setTroncato(el.scrollHeight > el.clientHeight + 1);
    };
    misura();
    window.addEventListener('resize', misura);
    return () => window.removeEventListener('resize', misura);
  }, [aperta, rubrica.descrizione]);

  const espandibile = haTesto && (troncato || aperta);

  return (
    <article
      className="rounded-2xl overflow-hidden transition-colors"
      style={{ backgroundColor: '#FFFFFF', border: '1px solid #EBEDF1' }}
    >
      <button
        type="button"
        onClick={() => espandibile && setAperta((v) => !v)}
        aria-expanded={espandibile ? aperta : undefined}
        className={`w-full text-left p-5 sm:p-6 ${espandibile ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-start gap-3.5">
          <span
            className="w-9 h-9 rounded-xl inline-flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(253,247,81,0.1)', border: '1px solid rgba(253,247,81,0.25)' }}
          >
            <Repeat size={16} style={{ color: Y }} />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-slate-900 leading-snug" style={{ letterSpacing: '-0.01em' }}>
                {rubrica.titolo}
              </h3>
              <span
                className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                style={{ color: '#5B6270', border: '1px solid #DDE0E6', letterSpacing: '.08em' }}
              >
                {rubrica.tipo === 'format' ? 'format' : 'rubrica'}
              </span>
            </div>

            {haTesto && (
              <>
                <p
                  ref={testoRef}
                  className="text-[15px] leading-relaxed whitespace-pre-line"
                  style={{
                    color: '#2B303A',
                    // chiusa: due righe di anteprima; aperta: testo intero
                    ...(aperta
                      ? {}
                      : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                  }}
                >
                  {rubrica.descrizione}
                </p>
                {espandibile && (
                  <span
                    className="inline-flex items-center gap-1 mt-2.5 text-[12px] font-semibold"
                    style={{ color: Y }}
                  >
                    {aperta ? 'Chiudi' : 'Leggi di più'}
                    <ChevronDown
                      size={13}
                      className="transition-transform"
                      style={{ transform: aperta ? 'rotate(180deg)' : 'none' }}
                    />
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </button>
    </article>
  );
};

/** Riquadro di una linea guida: titolo con icona + testo che rispetta gli a capo. */
const Blocco: React.FC<{
  icona: React.ReactNode;
  titolo: string;
  testo: string;
  colore?: string;
}> = ({ icona, titolo, testo, colore }) => (
  <div
    className="p-5 sm:p-6 rounded-2xl"
    style={{ backgroundColor: '#FFFFFF', border: '1px solid #EBEDF1' }}
  >
    <div className="flex items-center gap-2 mb-2.5" style={{ color: colore || Y }}>
      {icona}
      <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.16em' }}>{titolo}</span>
    </div>
    <p className="text-[15px] leading-relaxed whitespace-pre-line" style={{ color: '#13161C' }}>
      {testo}
    </p>
  </div>
);

const ClientGuidelines: React.FC<{ dati: Guidelines }> = ({ dati }) => {
  const { rubriche, creative } = dati;
  const colori = (creative?.colori || []).filter(pieno);
  // Due colonne solo se ci sono entrambe le cose: con una sola resterebbe
  // mezza pagina vuota, meglio una colonna di lettura.
  const dueColonne = rubriche.length > 0 && !!creative;

  return (
    <div className={`grid grid-cols-1 gap-x-10 gap-y-10 items-start ${dueColonne ? 'lg:grid-cols-2' : 'max-w-3xl'}`}>
      {/* Colonna 1 — Rubriche e format ricorrenti */}
      {rubriche.length > 0 && (
        <section>
          <div className="text-[11px] font-bold uppercase mb-1" style={{ color: Y, letterSpacing: '0.22em' }}>Rubriche</div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ letterSpacing: '-0.02em' }}>
            I format che portiamo avanti
          </h2>
          <p className="text-sm mb-6 max-w-2xl" style={{ color: '#5B6270' }}>
            Gli appuntamenti fissi dei tuoi contenuti: ogni rubrica ha un suo taglio e torna nel piano editoriale.
          </p>

          {/* Una card per rubrica, con anteprima di due righe: il riquadro
              separa le rubriche fra loro e il testo lungo si apre su richiesta,
              invece di riempire la pagina. */}
          <div className="space-y-3 sm:space-y-4">
            {rubriche.map((r) => (
              <CardRubrica key={r.id} rubrica={r} />
            ))}
          </div>
        </section>
      )}

      {/* Colonna 2 — Linee guida creative */}
      {creative && (
        <section>
          <div className="text-[11px] font-bold uppercase mb-1" style={{ color: Y, letterSpacing: '0.22em' }}>Linee guida</div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ letterSpacing: '-0.02em' }}>
            Come realizziamo i contenuti
          </h2>
          <p className="text-sm mb-6 max-w-2xl" style={{ color: '#5B6270' }}>
            Le regole condivise che seguiamo in fase di scrittura e di montaggio, così ogni contenuto resta riconoscibile.
          </p>

          <div className="space-y-4">
            {pieno(creative.obiettivi) && (
              <Blocco icona={<Target size={14} />} titolo="Obiettivi di comunicazione" testo={creative.obiettivi!} />
            )}
            {pieno(creative.taglio) && (
              <Blocco icona={<Film size={14} />} titolo="Taglio e stile dei reel" testo={creative.taglio!} />
            )}
            {pieno(creative.da_dire) && (
              <Blocco icona={<CheckCircle2 size={14} />} titolo="Cosa valorizzare" testo={creative.da_dire!} colore="#34d399" />
            )}
            {pieno(creative.da_non_dire) && (
              <Blocco icona={<Ban size={14} />} titolo="Cosa evitare" testo={creative.da_non_dire!} colore="#f87171" />
            )}
            {pieno(creative.note) && (
              <Blocco icona={<FileText size={14} />} titolo="Altre linee guida" testo={creative.note!} />
            )}

            {colori.length > 0 && (
              <div
                className="p-5 sm:p-6 rounded-2xl"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #EBEDF1' }}
              >
                <div className="text-[11px] font-bold uppercase mb-3" style={{ color: Y, letterSpacing: '0.16em' }}>Palette</div>
                <div className="flex flex-wrap gap-3">
                  {colori.map((c) => (
                    <span key={c} className="inline-flex items-center gap-2 text-[13px]" style={{ color: '#13161C' }}>
                      <span
                        className="w-5 h-5 rounded-md flex-shrink-0"
                        style={{ backgroundColor: c, border: '1px solid #DDE0E6' }}
                      />
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default ClientGuidelines;
